import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, XCircle } from 'lucide-react';
export function ProblemSection() {
  const treatments = [{
    name: 'Alpha-blockers',
    subtitle: '(Flomax, etc)',
    image: 'https://cdn.magicpatterns.com/uploads/kxXkZJc9r37ys9rnq1UiT2/6.svg',
    whatItDoes: 'Relax prostate and bladder muscles',
    whyItFails: 'Do nothing for the external vein pressure crushing the bladder.'
  }, {
    name: '5-ARIs',
    subtitle: '(Finasteride)',
    image: 'https://cdn.magicpatterns.com/uploads/1s8FKptUM68LnJ8Q3jT7zr/8.svg',
    whatItDoes: 'Shrink prostate tissue over months',
    whyItFails: 'Bladder still gets squeezed from outside; often comes with sexual side effects.'
  }, {
    name: 'Saw Palmetto',
    subtitle: '& similar herbs',
    image: 'https://cdn.magicpatterns.com/uploads/9Y5kSicVvhCHucEWBVUdWr/7.svg',
    whatItDoes: 'Supports prostate cells',
    whyItFails: 'Does not address circulation at all; JAMA study found it no better than placebo.'
  }];
  return <section className="py-20 bg-slate-900 relative overflow-hidden" data-id="element-222">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-223" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-224">
        {/* Header */}
        <div className="text-center mb-16" data-id="element-225">
          <span className="inline-block py-1 px-3 rounded-full bg-emerald-500/20 text-emerald-400 text-[19px] font-bold tracking-wider uppercase mb-4 border border-emerald-500/30" data-id="element-226">
            Doctor-Reviewed Formula
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-227">
            Here's What Your Urologist Never Told You About Nighttime Urgency...
          </h2>
          <p className="text-[19px] text-slate-300 leading-relaxed" data-id="element-228">
            You have been told your prostate is "normal for your age." You have
            been handed prescriptions. Maybe you have tried saw palmetto.{' '}
            <span className="font-semibold text-white" data-id="element-229">
              And you are still waking up 3, 4, even 5 times a night.
            </span>
          </p>
        </div>

        {/* The Trap Explanation */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-slate-800/50 rounded-2xl p-8 md:p-12 border border-slate-700 mb-16" data-id="element-230">
          <div className="flex flex-col md:flex-row gap-8 items-center" data-id="element-231">
            <div className="flex-1" data-id="element-232">
              <h3 className="text-3xl font-bold text-[#e6b500] mb-4" data-id="element-233">
                The Pelvic Pressure Trap Explained:
              </h3>
              <p className="text-slate-300 mb-4 leading-relaxed text-[19px]" data-id="element-234">
                Here is the uncomfortable truth: your bladder is not waking you
                up because it is full. It is being squeezed from the outside —
                even when it is nearly empty.
              </p>
              <p className="text-slate-300 mb-4 leading-relaxed text-[19px]" data-id="element-235">
                As men age, the tiny veins underneath and around the bladder
                begin losing their tone. At night, gravity causes blood to pool,
                creating a tight{' '}
                <span className="font-bold text-red-400" data-id="element-236">"pressure ring"</span>{' '}
                that mechanically compresses your bladder.
              </p>
              <div className="flex items-start space-x-3 bg-slate-900/50 p-4 rounded-lg border border-slate-700 shadow-sm" data-id="element-237">
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" data-id="element-238" />
                <p className="text-[19px] text-white font-medium" data-id="element-239">
                  Your brain screams "EMERGENCY!" so you rush to the bathroom...
                  and barely squeeze out a trickle. You weren't full. You were
                  being crushed.
                </p>
              </div>
            </div>
            <div className="w-full md:w-1/3 flex-shrink-0" data-id="element-240">
              {/* Anatomical Diagram */}
              <div className="aspect-[3/4] bg-slate-900/50 rounded-xl border-2 border-slate-700 flex flex-col items-center justify-center p-6 text-center relative overflow-hidden" data-id="element-241">
                <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-242" />
                <div className="w-32 h-32 rounded-full bg-red-900/30 border-4 border-red-700/50 flex items-center justify-center mb-4 relative z-10" data-id="element-243">
                  <div className="w-20 h-20 rounded-full bg-blue-900/30 border-4 border-blue-700/50 animate-pulse" data-id="element-244" />
                  <div className="absolute -right-2 top-0 bg-red-600 text-white text-[19px] font-bold px-2 py-1 rounded" data-id="element-245">
                    PRESSURE
                  </div>
                </div>
                <p className="text-[19px] font-bold text-slate-500 uppercase tracking-widest z-10" data-id="element-246">
                  Anatomical View
                </p>
                <p className="text-[19px] font-medium text-slate-300 mt-2 z-10" data-id="element-247">
                  Venous Pooling Compressing Bladder
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Dark Section for Comparison Table */}
      <div className="bg-[#020617] py-16" data-id="element-248">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-249">
          <div className="mb-16" data-id="element-250">
            <h3 className="text-3xl font-bold text-center text-white mb-8" data-id="element-251">
              This is why prostate pills do not work for most men:
            </h3>

            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-4" data-id="element-252">
              {treatments.map((treatment, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 space-y-3" data-id="element-253">
                  <div className="flex items-center gap-3" data-id="element-254">
                    <img src={treatment.image} alt={treatment.name} className="w-14 h-14 object-contain rounded-lg border border-slate-700" data-id="element-255" />
                    <div data-id="element-256">
                      <div className="font-bold text-white text-[19px]" data-id="element-257">
                        {treatment.name}
                      </div>
                      <div className="text-[19px] text-slate-400" data-id="element-258">
                        {treatment.subtitle}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-259">
                    <div data-id="element-260">
                      <div className="text-slate-500 font-semibold mb-1 text-[19px]" data-id="element-261">
                        What It Does:
                      </div>
                      <div className="text-slate-300 text-[19px]" data-id="element-262">
                        {treatment.whatItDoes}
                      </div>
                    </div>

                    <div data-id="element-263">
                      <div className="text-slate-500 font-semibold mb-1 flex items-center gap-1 text-[19px]" data-id="element-264">
                        <XCircle className="w-5 h-5 text-red-400" data-id="element-265" />
                        Why It Fails:
                      </div>
                      <div className="text-slate-300 pl-5 text-[19px]" data-id="element-266">
                        {treatment.whyItFails}
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* Desktop Table Layout */}
            <div className="hidden md:block overflow-hidden rounded-xl border border-slate-700 shadow-sm" data-id="element-267">
              <table className="w-full text-left border-collapse" data-id="element-268">
                <thead data-id="element-269">
                  <tr className="bg-slate-800 border-b border-slate-700" data-id="element-270">
                    <th className="p-6 text-[19px] font-bold text-white uppercase tracking-wider w-1/3" data-id="element-271">
                      What You've Tried
                    </th>
                    <th className="p-6 text-[19px] font-bold text-white uppercase tracking-wider w-1/3" data-id="element-272">
                      What It Does
                    </th>
                    <th className="p-6 text-[19px] font-bold text-white uppercase tracking-wider" data-id="element-273">
                      Why It Fails
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700 bg-slate-900/50" data-id="element-274">
                  {treatments.map((treatment, idx) => <tr key={idx} data-id="element-275">
                      <td className="p-6" data-id="element-276">
                        <div className="flex items-center gap-3" data-id="element-277">
                          <img src={treatment.image} alt={treatment.name} className="w-16 h-16 object-contain rounded-lg border border-slate-700" data-id="element-278" />
                          <div data-id="element-279">
                            <div className="font-bold text-white text-[19px]" data-id="element-280">
                              {treatment.name}
                            </div>
                            <div className="text-[19px] text-slate-400" data-id="element-281">
                              {treatment.subtitle}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-6 text-slate-300 text-[19px]" data-id="element-282">
                        {treatment.whatItDoes}
                      </td>
                      <td className="p-6" data-id="element-283">
                        <div className="flex items-start space-x-2" data-id="element-284">
                          <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-285" />
                          <span className="text-slate-300 text-[19px]" data-id="element-286">
                            {treatment.whyItFails}
                          </span>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            <div className="text-center mt-8" data-id="element-287">
              <p className="text-xl font-medium text-blue-400" data-id="element-288">
                The problem was never just your prostate.{' '}
                <br className="hidden md:block" data-id="element-289" />
                It was the{' '}
                <span className="font-bold underline decoration-emerald-400 decoration-4 underline-offset-2" data-id="element-290">
                  circulation collapse
                </span>{' '}
                underneath it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}